/**
 * Pinned tickets store — small pub-sub layer backed by localStorage.
 *
 * Architecture note for future Android-native port :
 * This file is the ONLY entry point that talks to the persistence layer
 * (localStorage today; AsyncStorage / SharedPreferences / a small SQLite
 * table tomorrow). Components consume it via the `usePinnedTickets()` hook
 * exclusively — they never read/write localStorage directly. To move to a
 * native Android floating-bubble service later, simply replace the
 * `_load` / `_save` implementations with the native bridge call ; every
 * consumer (UI bubbles, MyTicket pin button, layer renderer) will keep
 * working unchanged.
 *
 * Shape of the persisted state :
 *   {
 *     [ticketId: string]: {
 *       x: number | null,         // last manual position (px, fixed coords)
 *       y: number | null,
 *       minimized: boolean,       // collapsed to a small round badge
 *       pinnedAt: number,         // Date.now() when pinned (for stacking)
 *     }
 *   }
 */

const LS_KEY = "bypassgo_pinned_tickets";

function _load() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function _save(state) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — silent fallback, state still lives in memory */
  }
}

// In-memory mirror used as single source of truth at runtime.
let _state = _load();
const _listeners = new Set();

function _emit() {
  _save(_state);
  // Snapshot reference change so React detects updates
  _state = { ..._state };
  _listeners.forEach((l) => l(_state));
}

export function getPinnedTicketsState() {
  return _state;
}

export function subscribePinnedTickets(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

export function isTicketPinned(id) {
  return !!_state[id];
}

export function pinTicket(id, snapshot = null) {
  if (!id || _state[id]) return;
  _state[id] = {
    x: null,
    y: null,
    minimized: false,
    pinnedAt: Date.now(),
    // Persisted snapshot of the ticket payload used to render the bubble
    // BEFORE the next active-tickets poll lands. Without this, a freshly
    // pinned ticket can flash invisible for up to 3 s (the polling
    // interval). The snapshot is updated by the layer every time a
    // matching live ticket is observed, so the bubble always stays
    // consistent with the latest API data.
    snapshot: snapshot || null,
  };
  _emit();
}

/** Update the persisted snapshot for an already-pinned ticket. Idempotent
 *  no-op if the ticket isn't pinned. Skips the write when the snapshot
 *  is unchanged to avoid spamming localStorage. */
export function updatePinSnapshot(id, snapshot) {
  if (!id || !_state[id] || !snapshot) return;
  const current = _state[id].snapshot || null;
  // Cheap structural equality on the few fields the bubble reads.
  if (current
    && current.id === snapshot.id
    && current.status === snapshot.status
    && current.position === snapshot.position
    && current.code === snapshot.code
    && current.queueLetter === snapshot.queueLetter
    && current.queueId === snapshot.queueId
  ) {
    return;
  }
  _state[id] = { ..._state[id], snapshot };
  _emit();
}

export function unpinTicket(id) {
  if (!id || !_state[id]) return;
  delete _state[id];
  _emit();
}

export function updateBubbleState(id, partial) {
  if (!id || !_state[id]) return;
  _state[id] = { ..._state[id], ...partial };
  _emit();
}

export function toggleMinimized(id) {
  if (!id || !_state[id]) return;
  _state[id] = { ..._state[id], minimized: !_state[id].minimized };
  _emit();
}

/** Cleanup pins that are no longer present in the activeTickets list
 *  (ticket served / cancelled / expired). Idempotent. */
export function reconcilePins(activeIds) {
  const active = new Set(activeIds);
  let changed = false;
  for (const id of Object.keys(_state)) {
    if (!active.has(id)) {
      delete _state[id];
      changed = true;
    }
  }
  if (changed) _emit();
}
